import { useState } from 'react'
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query'
import { useInfiniteScroll } from '@/shared/composables/useInfiniteScroll'
import { ScrollTopButtonMobile } from '@/shared/ui/ScrollTopButton'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { EmptyStateCard } from '@/shared/ui/EmptyState'
import { Tap } from '@/shared/ui/Tap'
import type { Paged, SolutionCard } from '@/shared/lib/apiTypes'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { SolutionCardBody } from '@/shared/ui/SolutionCardRow'
import { EMPTY_ORG_CARD } from '@/public/features/home/constants/homeEmpty'
import { CERT_LABEL, useCommonCodes } from '@/shared/lib/codes'
import { RECENT_COUNT } from '@/public/features/problem/constants/list'

let lastSeenCount = RECENT_COUNT

export function SolutionListMobile({ query }: { query: UseInfiniteQueryResult<InfiniteData<Paged<SolutionCard>>> }) {
  const { dongLabel, isReady } = useCardLabels()
  const groups = useCommonCodes('category_group')
  const categories = useCommonCodes('category')

  const [group, setGroup] = useState<string | null>(null)

  const parentOf = new Map((categories.data ?? []).map((c) => [c.code, c.parentCode]))
  const endRef = useInfiniteScroll(
    () => void query.fetchNextPage(),
    query.hasNextPage && !query.isFetchingNextPage,
  )

  const all = query.data?.pages.flatMap((page) => page.items) ?? []

  const items =
    group === null
      ? all
      : all.filter((c) => (c.categoryCodes ?? []).some((code) => parentOf.get(code) === group))
  if (items.length > 0) lastSeenCount = items.length

  const card = (c: SolutionCard) => {
    // 기관 유형 라벨은 코드표가 아니라 화면 상수다(`CERT_LABEL`) — 값이 셋뿐이다.
    const cert = c.org.certType === null ? undefined : CERT_LABEL[c.org.certType]
    return (
      <SolutionCardBody
        card={c}
        dongLabel={dongLabel}
        {...(cert !== undefined ? { certLabel: cert } : {})}
      />
    )
  }

  const sections = (mode: 'skeleton' | 'data') => (
    <>
      <section className="flex flex-col gap-2.5 p-4 md:mx-auto md:max-w-[var(--list-w)] md:px-[var(--list-pad)] md:pt-[34px] md:pb-[30px]">
        <h2 className="m-0 text-body-lg leading-[1.4] font-semibold text-black-600">최근 등록된 기업</h2>
        <ul className="m-0 -mx-4 flex list-none snap-x snap-mandatory gap-3 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>li]:snap-start md:grid md:grid-cols-[repeat(var(--list-cols),minmax(0,1fr))] md:gap-[var(--list-gap)] md:overflow-visible md:m-0 md:px-0 md:[&>li]:w-auto md:[&>li>.list-card]:flex md:[&>li>.list-card]:min-h-[140px] md:[&>li>.list-card]:w-auto md:[&>li>.list-card]:flex-col md:[&>li>.list-card]:justify-between md:[&>li>.list-card]:rounded-[14px] md:[&>li>.list-card]:border md:[&>li>.list-card]:border-white-200 md:[&>li>.list-card]:bg-white-50 md:[&>li>.list-card]:px-5 md:[&>li>.list-card]:py-4 md:[&>li>.list-card]:shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
          {mode === 'skeleton' ? (
            <li>
              <span className="list-card--reserve list-card--reserve-box" />
            </li>
          ) : (
            all.slice(0, RECENT_COUNT).map((c) => (
              <li key={c.id}>
                <Link className="list-card list-card--box-light" to={`/solutions/${c.id}`}>
                  {card(c)}
                </Link>
              </li>
            ))
          )}
        </ul>
      </section>

      <section className="flex flex-col gap-3 p-4 md:mx-auto md:max-w-[var(--list-w)] md:px-[var(--list-pad)] md:pt-[34px] md:pb-[30px]">
        <div className="relative sticky top-[var(--sticky-top,0px)] z-20 before:absolute before:inset-y-0 before:left-1/2 before:-z-10 before:w-screen before:-translate-x-1/2 before:bg-white-50 flex min-w-0 gap-2 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:gap-y-2 md:overflow-x-visible" role="group" aria-label="분야">
          <Tap selected={group === null} onClick={() => setGroup(null)}>
            전체
          </Tap>
          {(groups.data ?? []).map((g) => (
            <Tap key={g.code} selected={group === g.code} onClick={() => setGroup(g.code)}>
              {g.label}
            </Tap>
          ))}
        </div>
        <ul className="m-0 flex list-none flex-col gap-4 p-0 md:grid md:grid-cols-[repeat(var(--list-cols),minmax(0,1fr))] md:gap-[var(--list-gap)] md:overflow-visible md:[&_.list-card]:flex md:[&_.list-card]:min-h-[140px] md:[&_.list-card]:w-auto md:[&_.list-card]:flex-col md:[&_.list-card]:justify-between md:[&_.list-card]:rounded-[14px] md:[&_.list-card]:border md:[&_.list-card]:border-white-200 md:[&_.list-card]:bg-white-50 md:[&_.list-card]:px-5 md:[&_.list-card]:py-4 md:[&_.list-card]:shadow-[0_0_4px_-1px_rgb(0_0_0/6%),0_0_12px_-4px_rgb(0_0_0/12%)]">
          {mode === 'skeleton'
            ? // 구분선은 **카드 사이**를 가르는 것이다 — 카드가 없는데 선만 떠 있으면 안 된다.
              Array.from({ length: lastSeenCount }, (_, i) => (
                <li key={i} className="flex flex-col gap-4 md:w-auto md:[&>.ui-divider-h]:hidden">
                  <span className="list-card--reserve" />
                </li>
              ))
            : items.map((c, i) => (
                <li key={c.id} className="flex flex-col gap-4 md:w-auto md:[&>.ui-divider-h]:hidden">
                  {i > 0 && <span className="ui-divider-h" aria-hidden="true" />}
                  <Link className="list-card" to={`/solutions/${c.id}`}>
                    {card(c)}
                  </Link>
                </li>
              ))}
        </ul>
      </section>
    </>
  )

  return (
    <DefaultLayout className="pc:hidden [--list-w:833px] [--list-pad:34px] [--list-gap:18px] [--list-cols:2] pc:[--list-w:1120px] pc:[--list-cols:3]">

      <QueryState
        isPending={query.isPending || !isReady}
        error={query.error}
        isEmpty={all.length === 0}
        empty={
          <section className="p-4">
            <EmptyStateCard {...EMPTY_ORG_CARD} />
          </section>
        }
        pending={sections('skeleton')}
      >
        {sections('data')}

      <div ref={endRef} aria-hidden="true" />
      {query.isFetchingNextPage && (
        <p className="m-0 py-4 text-center text-body-sm text-black-100">더 불러오는 중…</p>
      )}
      </QueryState>

      <ScrollTopButtonMobile />
    </DefaultLayout>
  )
}
